'use client';
import { useState } from 'react';
import { trackEvent } from '@/lib/analytics';
 import Icon from'@/components/ui/AppIcon';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    email: '',
    phone: '',
    product: '',
    quantity: '',
    message: '',
    agreedToPrivacy: false,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const countries = [
    'Belgium', 'Netherlands', 'Brazil', 'Morocco', 'Russia', 'Canada',
    'Germany', 'France', 'United Kingdom', 'Spain', 'Italy', 'Portugal',
    'United States', 'Mexico', 'Argentina', 'China', 'India', 'Japan',
    'Singapore', 'UAE', 'South Africa', 'Australia', 'Other'
  ]

  const products = [
    'Cooking Oils',
    'Wood Pellets',
    'Chicken Feed',
    'Fertilizers',
    'Iron Scraps',
    'Other'
  ]

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.country) newErrors.country = 'Country is required'
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }
    if (!formData.product) newErrors.product = 'Product selection is required'
    if (!formData.quantity.trim()) newErrors.quantity = 'Quantity is required'
    if (!formData.agreedToPrivacy) newErrors.agreedToPrivacy = 'You must agree to the privacy policy'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (validateForm()) {
      // Track conversion event
      trackEvent('form_submit', {
        form_id: 'contact_form',
        product: formData.product,
        country: formData.country,
        quantity: formData.quantity,
      });

      // Form is valid - in a real app, this would send to backend
      console.log('Form submitted:', formData)
      alert('Thank you for your inquiry! We will contact you within 24 hours.')
      
      // Reset form
      setFormData({
        name: '',
        country: '',
        email: '',
        phone: '',
        product: '',
        quantity: '',
        message: '',
        agreedToPrivacy: false,
      })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card rounded-organic p-8 shadow-lg">
      <div className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-2">
            Full Name <span className="text-error">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.name ? 'border-error' : 'border-border'
            } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors`}
            placeholder="John Doe"
          />
          {errors.name && <p className="mt-1 text-xs text-error">{errors.name}</p>}
        </div>

        {/* Country */}
        <div>
          <label htmlFor="country" className="block text-sm font-semibold text-foreground mb-2">
            Country <span className="text-error">*</span>
          </label>
          <select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.country ? 'border-error' : 'border-border'
            } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors`}
          >
            <option value="">Select your country</option>
            {countries.map((country) => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
          {errors.country && <p className="mt-1 text-xs text-error">{errors.country}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
            Email Address <span className="text-error">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.email ? 'border-error' : 'border-border'
            } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors`}
            placeholder="john@company.com"
          />
          {errors.email && <p className="mt-1 text-xs text-error">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
            placeholder="+32 4 123 4567"
          />
        </div>

        {/* Product */}
        <div>
          <label htmlFor="product" className="block text-sm font-semibold text-foreground mb-2">
            Product of Interest <span className="text-error">*</span>
          </label>
          <select
            id="product"
            name="product"
            value={formData.product}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-lg border ${
              errors.product ? 'border-error' : 'border-border'
            } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors`}
          >
            <option value="">Select a product</option>
            {products.map((product) => (
              <option key={product} value={product}>{product}</option>
            ))}
          </select>
          {errors.product && <p className="mt-1 text-xs text-error">{errors.product}</p>}
        </div>

        {/* Quantity */}
        <div>
          <label htmlFor="quantity" className="block text-sm font-semibold text-foreground mb-2">
            Quantity Needed <span className="text-error">*</span>
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className={`flex-1 px-4 py-3 rounded-lg border ${
                errors.quantity ? 'border-error' : 'border-border'
              } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors`}
              placeholder="e.g., 5000"
            />
            <select className="px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors">
              <option>kg</option>
              <option>tons</option>
              <option>liters</option>
              <option>units</option>
            </select>
          </div>
          {errors.quantity && <p className="mt-1 text-xs text-error">{errors.quantity}</p>}
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
            Brief Message
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors resize-none"
            placeholder="Tell us more about your requirements..."
          />
        </div>

        {/* Privacy Checkbox */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="agreedToPrivacy"
              checked={formData.agreedToPrivacy}
              onChange={handleChange}
              className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-muted-foreground">
              I agree to the <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> and consent to being contacted about my inquiry
            </span>
          </label>
          {errors.agreedToPrivacy && <p className="mt-1 text-xs text-error">{errors.agreedToPrivacy}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-semibold hover:bg-primary/90 shadow-lg btn-scale flex items-center justify-center gap-2"
        >
          Send Inquiry
          <Icon name="PaperAirplaneIcon" size={20} />
        </button>
      </div>
    </form>
  )
}